import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Home, AlertTriangle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { debugLog, debugLogError, startDebugSession } from '../lib/debugLogger'

// Same household-scoped key list as JoinHousehold.tsx — kept inline here
// rather than imported because the two files have diverged enough that a
// shared hook would obscure the per-flow behavior. If a third invite
// path appears, hoist this into src/lib/householdCache.ts.
const HOUSEHOLD_SCOPED_KEYS: string[][] = [
  ['householdId'],
  ['userRow'],
  ['user-onboarding'],
  ['household'],
  ['householdMembers'],
  ['household-schedule'],
  ['pantry-items'],
  ['confirmations'],
  ['latestListMeta'],
  ['listItems'],
  ['recipe-suggestions'],
]

interface Invite {
  id:           string
  household_id: string
  expires_at:   string
  used_at:      string | null
}

export default function Join() {
  const navigate     = useNavigate()
  const [params]     = useSearchParams()
  const queryClient  = useQueryClient()
  const { user, loading: authLoading } = useAuth()

  const code = params.get('code')?.toUpperCase().trim() ?? ''

  const [invite,      setInvite]      = useState<Invite | null>(null)
  const [memberName,  setMemberName]  = useState<string | null>(null)
  const [lookupDone,  setLookupDone]  = useState(false)
  const [error,       setError]       = useState('')
  const [joining,     setJoining]     = useState(false)

  // ── Look up the invite code ────────────────────────────────────────────────

  useEffect(() => {
    if (!code) {
      setError('No invite code found in this link.')
      setLookupDone(true)
      return
    }

    async function lookup() {
      const { data: inv, error: invErr } = await supabase
        .from('invites')
        .select('id, household_id, expires_at, used_at')
        .eq('invite_code', code)
        .maybeSingle()

      if (invErr || !inv) {
        setError('This invite link has expired.')
        setLookupDone(true)
        return
      }

      if (inv.used_at !== null) {
        setError('This invite link has expired.')
        setLookupDone(true)
        return
      }

      if (new Date(inv.expires_at) < new Date()) {
        setError('This invite link has expired.')
        setLookupDone(true)
        return
      }

      setInvite(inv)

      const { data: member } = await supabase
        .from('users')
        .select('display_name, email')
        .eq('household_id', inv.household_id)
        .limit(1)
        .maybeSingle()

      setMemberName(member?.display_name ?? member?.email ?? null)
      setLookupDone(true)
    }

    lookup()
  }, [code])

  // ── Join handler ───────────────────────────────────────────────────────────

  async function handleJoin() {
    if (!invite || !user) return
    setJoining(true)
    setError('')
    startDebugSession('household-invite-accept')
    void debugLog({
      feature: 'household',
      step:    '1-accept-start',
      payload: { invite_id: invite.id, invite_household_id: invite.household_id, flow: 'Join.tsx-code' },
    })

    try {
      const { error: updateErr } = await supabase
        .from('users')
        .update({ household_id: invite.household_id })
        .eq('id', user.id)
      if (updateErr) throw updateErr
      void debugLog({
        feature: 'household',
        step:    '2-user-household-updated',
        payload: { to: invite.household_id },
      })

      const { error: usedErr } = await supabase
        .from('invites')
        .update({ used_at: new Date().toISOString(), used_by: user.id })
        .eq('id', invite.id)
      if (usedErr) throw usedErr
      void debugLog({ feature: 'household', step: '3-invite-marked-used' })

      // Seed the new household_id into the React Query cache so the
      // /home render after navigate() sees the correct context — same
      // pattern as JoinHousehold.tsx.
      queryClient.setQueryData(['householdId', user.id], invite.household_id)
      const { data: refreshedUser } = await supabase
        .from('users')
        .select('id, household_id, display_name, email, restock_notifications_enabled')
        .eq('id', user.id)
        .single()
      if (refreshedUser) {
        queryClient.setQueryData(['userRow', user.id], refreshedUser)
      }
      void debugLog({
        feature: 'household',
        step:    '4-cache-seeded',
        payload: { newHouseholdId: refreshedUser?.household_id ?? null },
      })

      // Force every household-scoped query to refetch.
      for (const key of HOUSEHOLD_SCOPED_KEYS) {
        queryClient.invalidateQueries({ queryKey: key })
      }
      void debugLog({ feature: 'household', step: '5-cache-invalidated' })

      void debugLog({ feature: 'household', step: '6-navigating-home' })
      navigate('/home')
    } catch (err) {
      void debugLogError('household', 'accept', err)
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setJoining(false)
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  if (authLoading || !lookupDone) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center">
        <span className="h-8 w-8 rounded-full border-2 border-forest border-t-transparent animate-spin" />
      </main>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-6 pt-12">
      <div className="w-full max-w-[400px] flex flex-col items-center text-center">

        {/* App mark — paper tile with lucide Home (no emoji) */}
        <div className="h-16 w-16 rounded-2xl border border-border-sand bg-paper flex items-center justify-center mb-6">
          <Home className="h-7 w-7 text-forest" strokeWidth={1.5} />
        </div>

        {error ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.18em] text-terracotta font-medium mb-3">
              Invite
            </p>
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-terracotta" strokeWidth={1.8} />
              <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em]">
                Invite <em className="italic">expired</em>
              </h1>
            </div>
            <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-6">
              {error}
            </p>
            <button
              type="button"
              onClick={() => navigate('/sign-in')}
              className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
            >
              Go to sign in
            </button>
          </>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-3">
              You're invited
            </p>
            <h1 className="font-serif text-[26px] text-ink leading-[1.1] tracking-[-0.015em] mb-2">
              {memberName
                ? <>{memberName}'s <em className="italic">pantry</em></>
                : <>Join a shared <em className="italic">pantry</em></>}
            </h1>
            <p className="font-serif italic text-[14px] text-ink-muted leading-[1.5] mb-7 max-w-[300px]">
              Share groceries, save together.
            </p>

            {!user ? (
              <div className="w-full flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/sign-in?redirect=/join?code=${code}`)}
                  className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
                >
                  Sign in to join
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/sign-up?redirect=/join?code=${code}`)}
                  className="w-full rounded-2xl border border-border-sand bg-paper py-3.5 font-serif text-[14px] font-medium text-ink"
                >
                  Create account
                </button>
                <p className="font-serif italic text-[11px] text-ink-muted mt-3 leading-[1.5]">
                  You'll keep your existing data. Joining adds shared pantry access.
                </p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={joining}
                  className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-60"
                >
                  {joining
                    ? 'Joining…'
                    : memberName
                      ? `Accept invite`
                      : 'Accept invite'}
                </button>
                {error && (
                  <p className="font-serif italic text-[12px] text-terracotta text-center mt-3">
                    {error}
                  </p>
                )}
                <p className="font-serif italic text-[11px] text-ink-muted mt-4 leading-[1.5]">
                  You'll keep your existing data — joining adds shared pantry access.
                </p>
              </>
            )}
          </>
        )}

      </div>
    </main>
  )
}
